import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, motion } from "framer-motion"

import { CheckMarkIcon } from "@/3514/assets"
import {
    ESpecialChar,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    EWaveLoaderVariant,
    ErrorHint,
    SpecialChars,
    Typography,
    WaveLoader,
    renderSessionInfoHeaderAvatar
} from "@/3514/components"
import { THEME } from "@/3514/theme"
import { ESessionModality } from "@/3514/types"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import classes from "./attendance.modal.styles"
import type { TAttendanceModalParticipantMark as TMarkState } from "./attendance.modal.ui.types"
import uiUtils from "./attendance.modal.ui.utils"

type TCheckBadgeProps = { isSelected?: boolean; variant: TMarkState; action(state: TMarkState): void }
const CheckBadge: FC<TCheckBadgeProps> = ({ isSelected, variant, action }: TCheckBadgeProps): ReactElement => (
    <motion.button
        type="button"
        tabIndex={0}
        whileFocus={{ scale: 1.075 }}
        whileHover={{ scale: 1.075 }}
        transition={{ duration: 0.2, ease: "easeIn" }}
        onClick={withPressHandler((): void => action(variant))}
        onKeyDown={withPressHandler((): void => action(variant))}
        className={classes.attendance.container.participants.item.mark({ isSelected, state: variant })}
    >
        {variant === "present" && <CheckMarkIcon fillColor={THEME.COLORS.gray[100]} />}
        {variant === "not_present" && SpecialChars[ESpecialChar.Cross]}
    </motion.button>
)

const Loader: FC = (): ReactElement => (
    <motion.div
        initial={{ scale: 0.9, opacity: 0.9 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0.9 }}
        layout
        layoutId="attendance-modal-loader"
        transition={{ duration: 0.3 }}
        style={{ minHeight: "100px", display: "flex" }}
    >
        <WaveLoader cssIdentifier="root-loader" withRelativePosition variant={EWaveLoaderVariant.LightBlue} />
    </motion.div>
)

type TNotPresenceProps = { isVisible: boolean; participantsAmount: number }
const NotPresenceWarning: FC<TNotPresenceProps> = ({
    isVisible,
    participantsAmount
}: TNotPresenceProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    return (
        <AnimatePresence initial={false} mode="wait">
            {isVisible && (
                <motion.div className={classes.attendance.container.notPresenceWarning()}>
                    <ErrorHint
                        isVisible
                        variant="error-secondary"
                        text={t("modals.attendance.notAbsenceWarning.title")}
                        withNoAnimatePresence
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                    >
                        <Typography
                            text={t("modals.attendance.notAbsenceWarning.description", {
                                context: participantsAmount === 1 ? String("") : String("few")
                            })}
                            size={ETypographySize.Tiny}
                            tag={ETypographyTag.Paragraph}
                            color={ETypographyColor.Dark}
                            alignment={ETypographyAlignment.Center}
                            className={classes.attendance.container.notPresenceWarningDescription()}
                            isItalic
                            isPretty
                        />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

function SessionInfo({
    duration,
    startDate,
    isGuest,
    modality
}: {
    duration: number
    startDate: string
    isGuest: boolean
    modality: ESessionModality
}): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    return (
        <article className={classes.attendance.sessionInfo.container()}>
            {renderSessionInfoHeaderAvatar(modality, { isGuest, size: 40 })()}

            <div className={classes.attendance.sessionInfo.timeWrapper()}>
                <Typography
                    text={uiUtils.getHeaderFormattedDate(startDate)}
                    color={ETypographyColor.Black}
                    size={ETypographySize.Small}
                    weight={ETypographyWeight.Bold}
                />

                <Typography
                    text={
                        <Fragment>
                            {duration}
                            {SpecialChars[ESpecialChar.EmptySpace]}
                            {t("common.min")}
                        </Fragment>
                    }
                    color={ETypographyColor.Dark}
                    size={ETypographySize.Small}
                />
            </div>
        </article>
    )
}
SessionInfo.displayName = "AttendanceModalSessionInfo"

export default { NotPresenceWarning, Loader, CheckBadge, SessionInfo }
